import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';

function HeroSection() {
  const { t } = useTranslation();
  const [loaded, setLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLoaded(true);
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Industrial%20steel%20wire%20coils%20and%20precision%20machinery%20in%20a%20German%20manufacturing%20facility%2C%20large%20metal%20wire%20spools%20stacked%20in%20a%20modern%20factory%20hall%2C%20dramatic%20industrial%20lighting%2C%20metallic%20silver%20and%20steel%20tones%2C%20professional%20manufacturing%20photography%2C%20high%20detail%2C%20warm%20ambient%20light&width=1600&height=900&seq=hero-wire-2026&orientation=landscape"
          alt="Industrial manufacturing machinery"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-950/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 w-full">
        <div className={`max-w-2xl transition-all duration-1000 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500/20 backdrop-blur-sm rounded-full mb-6 border border-accent-500/30">
            <span className="w-2 h-2 rounded-full bg-accent-400"></span>
            <span className="text-accent-300 text-sm font-medium uppercase tracking-wider">
              {t('common.madeInGermany')}
            </span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-6">
            {t('hero.title')}
          </h1>

          <p className="font-heading text-2xl sm:text-3xl md:text-4xl text-accent-400 font-medium mb-8">
            {t('hero.subtitle')}
          </p>

          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-4 max-w-xl">
            {t('hero.description')}
          </p>

          <p className="text-white/60 text-sm md:text-base leading-relaxed max-w-xl mb-10">
            {t('hero.description2')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/products"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/25 whitespace-nowrap"
            >
              {t('nav.products')}
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg border border-white/20 transition-all duration-300 whitespace-nowrap"
            >
              {t('nav.contact')}
              <i className="ri-phone-line"></i>
            </Link>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 transition-all duration-1000 delay-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}>
        <div className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center pt-2">
          <div className="w-1.5 h-1.5 rounded-full bg-white/60 animate-bounce"></div>
        </div>
      </div>
    </section>
  );
}

function AboutSection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div>
            <span className="text-primary-500 text-sm font-medium uppercase tracking-wider mb-4 block">
              {t('about.title')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-6 leading-tight">
              {t('about.subtitle')}
            </h2>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8">
              {t('about.description')}
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-primary-600 font-medium hover:text-primary-700 transition-colors group"
            >
              {t('common.learnMore')}
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20German%20industrial%20factory%20interior%20with%20precision%20machinery%2C%20clean%20bright%20workshop%2C%20steel%20and%20metal%20processing%20equipment%2C%20professional%20industrial%20photography%2C%20warm%20natural%20lighting&width=800&height=600&seq=about-factory&orientation=landscape"
                alt="Modern manufacturing facility"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 md:-bottom-8 md:-left-8 bg-accent-500 text-white rounded-xl p-5 md:p-6 shadow-lg">
              <div className="font-heading text-3xl md:text-4xl font-bold">1966</div>
              <div className="text-accent-100 text-sm mt-1">Seit der Gründung</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactSection() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 md:py-28 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="relative order-2 lg:order-1">
            <div className="rounded-2xl overflow-hidden shadow-xl bg-white">
              <img
                src="https://www.bock-mf.de/typo3temp/pics/1ba071bf53.png"
                alt="Jutta Kordt-Bock"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="mt-4 text-center">
              <p className="font-heading text-lg md:text-xl font-semibold text-foreground-900">
                Jutta Kordt-Bock
              </p>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="text-primary-500 text-sm font-medium uppercase tracking-wider mb-4 block">
              {t('nav.contact')}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-6 leading-tight">
              {t('contactSection.title')}
            </h2>
            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-xl text-primary-500"></i>
                </div>
                <div>
                  <p className="text-foreground-500 text-sm mb-1">{t('contactSection.phone')}</p>
                  <a href="tel:+49235152133" className="text-foreground-900 text-xl md:text-2xl font-heading font-semibold hover:text-primary-600 transition-colors">
                    {t('contactSection.phoneNumber')}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center flex-shrink-0">
                  <i className="ri-mail-line text-xl text-primary-500"></i>
                </div>
                <div>
                  <p className="text-foreground-500 text-sm mb-1">{t('contactSection.email')}</p>
                  <a href="mailto:sales@bock-mf.de" className="text-foreground-900 text-xl md:text-2xl font-heading font-semibold hover:text-primary-600 transition-colors">
                    {t('contactSection.emailAddress')}
                  </a>
                </div>
              </div>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-primary-500/25"
            >
              {t('nav.contact')}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <Layout>
      <HeroSection />
      <AboutSection />
      <ContactSection />
    </Layout>
  );
}